import React, { useEffect, useState } from "react";
import {
  MenuOutlined,
  UserOutlined,
  EyeOutlined,
  AppstoreOutlined,
  LoadingOutlined,
  LogoutOutlined,
  ApartmentOutlined,
  CrownOutlined,
  IdcardOutlined,
  CaretDownOutlined,
  PlusOutlined,
  ClockCircleFilled,
  ReloadOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Menu,
  Tag,
  Tooltip,
  message,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { AuthApi, ProjectApi } from "../../../services";
import { actions as authActions } from "../../../store/authRedux/actions";
import { actions as commonActions } from "../../../store/commonRedux/actions";
import styles from "./styles.module.css";
import { useMediaQuery, useModules } from "../../../hooks";
import { helpers } from "../../../utils";
import {
  getPermissionedMenuItems,
  hasPermission,
} from "../../../utils/helpers";
import { toPath } from "../../navPaths";
import RegisterOrgModal from "../RegisterOrg";
import { switchOrganization } from "../../../utils/orgSwitch";
import { ChevronDown, Moon, Sun } from "react-feather";
import VerifyActivityModal from "../../../pages/Projects/screens/Edit/partials/Kanban/singleTask/partials/verifyActivityModal";
import { CountUpTimer } from "../../../components";
import { useTheme, useTimeTracking } from "../../../hooks";
import UserAvatar from "../../../components/UserAvatar";

function getItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

const findMenuItem = (items, key) => {
  for (const item of items || []) {
    if (String(item?.key) === String(key)) return item;
    const child = findMenuItem(item?.children, key);
    if (child) return child;
  }
  return null;
};

const Header = () => {
  const isMobile = useMediaQuery("(max-width: 992px)");

  const { activeTracking, setTracking } = useTimeTracking();
  const user = useSelector(({ auth }) => auth.user);
  const organization = useSelector(({ auth }) => auth.organization);
  const allOrganizations = useSelector(({ auth }) => auth.allOrganizations);
  /* عضویت‌های مشتری کنار سازمان‌ها می‌نشینند: کسی که هم جایی همکار است و هم جایی
     مشتری، بدون این هیچ راهی برای رسیدن به پرتال ندارد. برای بقیه خالی است و
     چیزی به منو اضافه نمی‌شود. */
  const customerOf = useSelector(({ auth }) => auth.customerOf);
  const portalCustomer = useSelector(({ auth }) => auth.portalCustomer);

  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [verifyModal, setVerifyModal] = useState({ show: false, data: null });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDark, toggle: toggleTheme } = useTheme();
  const { visible: modules, has: hasModule } = useModules();
  const canManageSubscription =
    helpers.IsUerOwner() || hasPermission("manage_org");

  const logoutMutate = useMutation(AuthApi.logoutUser, {
    onSuccess: (res) => {
      message.success({ content: "با موفقیت خارج شدید" });
      dispatch(authActions.logout());
    },
    onError: (error) => {
      if (error.response.status == 400) {
        message.error({ content: "خطا در ثبت داده" });
      }
    },
  });

  const profileItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "پروفایل من",
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "خروج از حساب",
      danger: true,
      disabled: logoutMutate.isLoading,
    },
  ];

  const organizations = Array.isArray(allOrganizations) ? allOrganizations : [];
  const organizationLimitReached = organizations.length >= 3;
  const organizationItems = [
    ...organizations.map((org) => ({
      key: `organization:${org.uuid}`,
      label: (
        <span className={styles.workspaceOption}>
          <Avatar
            size={26}
            src={org.avatar?.original_url}
            icon={<ApartmentOutlined />}
          />
          <span className={styles.workspaceOptionText}>
            <strong>{org?.title}</strong>
            {org.uuid === organization?.uuid && <small>سازمان فعلی</small>}
          </span>
        </span>
      ),
    })),
    ...(customerOf?.length
      ? [
          { type: "divider" },
          ...customerOf.map((membership) => ({
            key: `portal:${membership.customer_id}`,
            label: (
              <span className={styles.workspaceOption}>
                <Avatar size={26} icon={<IdcardOutlined />} />
                <span className={styles.workspaceOptionText}>
                  <strong>{membership.organization?.title}</strong>
                  <small>پرتال مشتری</small>
                </span>
              </span>
            ),
          })),
        ]
      : []),
    { type: "divider" },
    {
      key: "new-organization",
      icon: <PlusOutlined />,
      disabled: organizationLimitReached,
      label: organizationLimitReached
        ? "حداکثر ۳ سازمان"
        : "افزودن سازمان جدید",
    },
  ];

  const handleProfileMenuClick = ({ key }) => {
    if (key === "profile") navigate("/profile");
    if (key === "logout") logoutMutate.mutate();
  };

  const handleOrganizationMenuClick = ({ key }) => {
    if (key === "new-organization") {
      if (!organizationLimitReached) setCreateOrgOpen(true);
      return;
    }
    if (key.startsWith("portal:")) {
      const membership = customerOf?.find(
        (item) => String(item.customer_id) === key.replace("portal:", ""),
      );
      if (membership) {
        queryClient.clear();
        dispatch(authActions.setOrganization(membership.organization));
        dispatch(authActions.setPortalCustomer(membership));
      }
      return;
    }

    const uuid = key.replace("organization:", "");
    const target = organizations.find((org) => org.uuid === uuid);
    if (target && target.uuid !== organization?.uuid) {
      switchOrganization(target, queryClient, dispatch);
    }
  };

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    /* پروژه‌های سایدبار اندپوینت پنل‌اند؛ نماینده‌ی مشتری اجازه‌اش را ندارد. */
    enabled: !portalCustomer,
    queryKey: ["[List] Projects Sidebar"],
    queryFn: () =>
      ProjectApi.getProjects({
        params: {
          page_size: 5,
        },
      }).then((result) => {
        return result.data.projects;
      }),
    initialData: [],
    cacheTime: 0,
  });

  const items = getPermissionedMenuItems(
    getItem("پروژه ها", "projects", <AppstoreOutlined />, [
      { label: "همه", key: "projects" },

      { label: "پروژه جدید", key: "/projects?action=new", icon: <PlusOutlined /> },
      ...result?.slice(0, 5).map((res) => ({
        label: res.title,
        key: `/projects/edit/${res.uuid}`,
      })),
    ]),
    modules,
  );

  const getRemainDate = (date) => {
    return Math.floor((new Date(date) - new Date()) / 86400000);
  };

  /* A plan with no expiry date has no day count to show — the badge just says
   * the subscription is active, instead of printing a number derived from
   * nothing. */
  const planRemainDays = organization?.plan_expire_at
    ? Math.max(getRemainDate(organization.plan_expire_at), 0)
    : null;

  return (
    <>
      <RegisterOrgModal isOpen={createOrgOpen} setIsOpen={setCreateOrgOpen} />
      {hasModule("time_tracking") && (
        <VerifyActivityModal
          isOpen={verifyModal.show}
          setIsOpen={(state) => setVerifyModal({ show: state, data: null })}
          defaultValue={verifyModal?.data}
          taskId={activeTracking?.task_id}
          onFinish={(res) => {
            queryClient.invalidateQueries({
              queryKey: [
                `[Single-${res?.task_id ?? activeTracking?.task_id}] Task`,
              ],
            });
            setTracking(res);
            message.success({ content: "فعالیت با موفقیت ثبت شد" });
          }}
        />
      )}
      <div
        className="w-100 py-3 px-3 px-lg-4 ds-topbar-mobile position-fixed top-0 d-flex justify-content-between mx-auto"
        style={{ zIndex: 1000, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center"
            onClick={() => setOpen(true)}
            style={{ cursor: "pointer" }}
          >
            <MenuOutlined
              width={48}
              size={48}
              style={{ fontSize: 20 }}
              className={isMobile && "menu-side"}
            />
          </div>
          <Dropdown
            menu={{
              items: organizationItems,
              onClick: handleOrganizationMenuClick,
            }}
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName={styles.accountDropdown}
          >
            <button type="button" className={styles.organizationTrigger}>
              <div className="d-flex align-items-center gap-1">
                {organization?.avatar && (
                  <Avatar
                    size={"small"}
                    src={organization.avatar?.original_url}
                  />
                )}
                <h6
                  className="mb-0 fw-bold"
                  style={{
                    maxWidth: isMobile ? 120 : 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: isMobile ? 14 : undefined,
                  }}
                >
                  {organization?.title}
                </h6>
              </div>
              <ChevronDown style={{ width: 16, flexShrink: 0 }} />
            </button>
          </Dropdown>
          {!portalCustomer && canManageSubscription && !isMobile && (
            <Tooltip
              title={
                organization?.hasPlan ? "مدیریت اشتراک" : "خرید اشتراک"
              }
            >
              <Link
                to={"/subscriptions"}
                className={styles.subBadge}
                aria-label={
                  organization?.hasPlan ? "مدیریت اشتراک" : "خرید اشتراک"
                }
              >
                {organization?.hasPlan ? (
                  <Tag
                    style={{ fontSize: 14, padding: 8 }}
                    className="d-inline-flex align-items-center gap-1"
                    color="success"
                    bordered={false}
                  >
                    <CrownOutlined color="success" />
                    <span>
                      اشتراک دارید
                      {planRemainDays !== null ? ` - ${planRemainDays} روز` : ""}
                    </span>
                    <LeftOutlined className={styles.subBadgeArrow} />
                  </Tag>
                ) : (
                  <Tag
                    style={{ fontSize: 14, padding: 8 }}
                    className="d-inline-flex align-items-center gap-1"
                    color="error"
                    bordered={false}
                  >
                    <CrownOutlined color="error" />
                    <span>اشتراک ندارید</span>
                    <LeftOutlined className={styles.subBadgeArrow} />
                  </Tag>
                )}
              </Link>
            </Tooltip>
          )}
        </div>
        <div className="d-flex align-items-lg-center gap-2">
          <Button
            type="text"
            shape="circle"
            onClick={toggleTheme}
            icon={
              isDark ? (
                <Sun style={{ width: 15, color: "var(--ds-text-secondary)" }} />
              ) : (
                <Moon
                  style={{ width: 15, color: "var(--ds-text-secondary)" }}
                />
              )
            }
            style={{ width: 32, height: 32 }}
          />
          <div className="ms-2 d-flex align-items-center">
            {!portalCustomer && hasModule("time_tracking") && activeTracking && (
              <div
                style={{
                  backgroundColor: "var(--ds-accent)",
                  color: "white",
                  borderRadius: "16px",
                  padding: "4px 8px",
                  cursor: "pointer",
                }}
                className="d-flex align-items-center gap-1"
                onClick={() =>
                  setVerifyModal({
                    show: true,
                    data: { ...activeTracking, to: new Date() },
                  })
                }
              >
                <ClockCircleFilled style={{ color: "white" }} />
                <CountUpTimer
                  startDate={new Date(activeTracking.from)}
                  color="white"
                  fontSize={10}
                />
              </div>
            )}
          </div>
          <Dropdown
            menu={{ items: profileItems, onClick: handleProfileMenuClick }}
            trigger={["click"]}
            placement="bottomLeft"
            overlayClassName={styles.accountDropdown}
          >
            <button type="button" className={styles.profileTrigger}>
              {!isMobile &&
                `${user?.first_name || ""} ${user?.last_name || ""}`}
              <CaretDownOutlined size={12} twoToneColor="#000" />
              {logoutMutate.isLoading ? (
                <LoadingOutlined spin={true} />
              ) : (
                /* antd's Badge offsets the dot from the square bounding box, so
                   on a round avatar it floated off the rim. Sizing the dot from
                   the avatar itself keeps it seated on the circle at 32px and
                   40px alike. */
                <span
                  className={styles.presence}
                  style={{ "--avatar-size": `${isMobile ? 32 : 40}px` }}
                >
                  <UserAvatar user={user} size={isMobile ? 32 : 40} />
                  <span className={styles.presenceDot} aria-hidden="true" />
                </span>
              )}
            </button>
          </Dropdown>
        </div>
      </div>
      <Drawer
        title={
          <div
            className={styles.logo_con}
            data-tut={isMobile && "start_tour_logo"}
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/");
              setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              navigate("/");
              setOpen(false);
            }}
          >
            <div className={styles.logo}>
              <img width={32} height={32} src="/logo-bg-trans.png" alt="تسکامون" />
            </div>
            <h4 className={styles.logo_title}>تسکامون</h4>
          </div>
        }
        placement={"right"}
        width={300}
        onClose={() => setOpen(false)}
        open={open}
      >
        <div className="w-100 h-100 d-flex flex-column justify-content-between">
          <Menu
            onClick={(e) => {
              const item = findMenuItem(items, e.key);
              if (item?.locked) {
                dispatch(
                  commonActions.setSubModal({
                    feature: item.key,
                    title: item.label,
                  }),
                );
                setOpen(false);
                return;
              }
              navigate(toPath(e.key));
              setOpen(false);
            }}
            mode="inline"
            items={[...items]}
          />
          <div className="d-flex flex-column align-items-center gap-2">
            {canManageSubscription && (
              <Link
                to={"/subscriptions"}
                className={`mb-2 w-100 d-flex ${styles.subBadge}`}
                onClick={() => {
                  setOpen(false);
                }}
              >
                {organization?.hasPlan ? (
                  <Tag
                    style={{ fontSize: 14, padding: 8 }}
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    color="success"
                    bordered={false}
                  >
                    <CrownOutlined color="success" />
                    <span>
                      اشتراک دارید
                      {planRemainDays !== null ? ` - ${planRemainDays} روز` : ""}
                    </span>
                    <LeftOutlined className={styles.subBadgeArrow} />
                  </Tag>
                ) : (
                  <Tag
                    style={{ fontSize: 14, padding: 8 }}
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    color="error"
                    bordered={false}
                  >
                    <CrownOutlined color="error" />
                    <span>اشتراک ندارید</span>
                    <LeftOutlined className={styles.subBadgeArrow} />
                  </Tag>
                )}
              </Link>
            )}
            {}
            {}
            <Button
              icon={<LogoutOutlined />}
              loading={logoutMutate.isLoading}
              type="text"
              danger
              onClick={() => logoutMutate.mutate()}
            >
              خروج از حساب کاربری
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default Header;
